import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import type {
  AdvanceRefundListRequest,
  PatientRefundsListResponse,
} from '@/shared/types/billing';

/**
 * Patient-scoped refund list — POST `/api/Billing/Payment/Refunds/Patient/{patientId}`.
 *
 * Distinct from invoice-scoped refund-fetch `useRefundsForInvoice`
 * (`use-payments.ts:53` → `/Payment/Refunds/invoice/{invoiceId}`). The
 * patient-scoped endpoint returns refunds across ALL receipts for the
 * patient (advance + invoice combined), with paging.
 *
 * Wire (Flutter `patient_billing_data_source_impl.dart:89-101` +
 * **dev curl 2026-05-20**):
 *
 *   POST /api/Billing/Payment/Refunds/Patient/{patientId}
 *        body: AdvanceRefundListRequest = {
 *          query,           // free-text search (default "")
 *          startDate,       // no-Z ISO
 *          endDate,         // no-Z ISO
 *          refundMethod,    // string[] — MUST be [] for no filter, not null (backend 500s on null)
 *          pageNumber,
 *          pageSize,
 *        }
 *        → PagedResults<PatientRefundRow> = {
 *            pageNumber, pageSize,
 *            totalNumberOfPages, totalNumberOfRecords,
 *            results: PatientRefundRow[],
 *          }
 *
 * **Two divergences from Flutter source captured by the live curl** (see
 * `shared/types/billing.ts` doc comments for full notes):
 *   1. `refundMethod: null` → backend 500. Must ship `[]` for no-filter.
 *      Flutter `AdvanceRefundListRequest.toJson` ships null when null,
 *      but the path is never exercised (no Flutter UI dispatcher).
 *   2. Response envelope is `PagedResults<T>` (generic .NET shape), not
 *      Flutter's documented `PatientRefundListModel`
 *      (`count/currentPage/totalPage/refundList`). Flutter model file is
 *      stale — never deserialized in practice.
 *
 * Per CLAUDE.md precedence rule: live dev curl wins over Flutter source
 * for wire shape, body, nullable defaults, date formatting.
 */

export interface UsePatientRefundsListParams {
  patientId: string | null | undefined;
  pageNumber?: number;
  pageSize?: number;
  query?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  refundMethod?: string[] | null;
}

export function usePatientRefundsList(params: UsePatientRefundsListParams) {
  const body: AdvanceRefundListRequest = {
    query: params.query ?? null,
    startDate: params.startDate ?? null,
    endDate: params.endDate ?? null,
    // Empty array — NOT null — when no filter. Backend 500s on
    // `refundMethod: null`. See file header.
    refundMethod: params.refundMethod ?? [],
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 20,
  };

  return useQuery({
    queryKey: [
      'patient-refunds-list',
      params.patientId,
      body.pageNumber,
      body.pageSize,
      body.query,
      body.startDate,
      body.endDate,
      body.refundMethod,
    ],
    queryFn: ({ signal }) =>
      post<PatientRefundsListResponse>(
        `${BILLING_BASE}/Payment/Refunds/Patient/${params.patientId}`,
        body,
        { signal },
      ),
    enabled: !!params.patientId,
    placeholderData: (prev) => prev,
  });
}
